'use client';

import { Avatar, Spinner } from '@common/components';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useInView } from 'react-intersection-observer';

import { UnauthInfiniteQueryOptions } from '@/api';
import { filledInfiniteQueryOptions } from '@/utils/filledInfiniteQueryOptions';

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 20;

interface TabContentDonationsProps {
  donationBusinessId: number;
}

/**
 * * 지정기부사업 상세 - 기부내역 탭
 */
export const TabContentDonations = ({
  donationBusinessId,
}: TabContentDonationsProps) => {
  const { data, fetchNextPage, hasNextPage, isFetching, isPending } =
    useInfiniteQuery({
      ...filledInfiniteQueryOptions(
        UnauthInfiniteQueryOptions[
          'get:/wello/v2/allows/home-donation/by-donation-business'
        ]({
          query: {
            donationBusinessId,
            page: INITIAL_PAGE,
            size: INITIAL_SIZE,
          },
        }),
      ),
    });

  const { ref } = useInView({
    onChange: (inView) => {
      if (!isFetching && inView) fetchNextPage();
    },
  });

  const list =
    data?.pages.flatMap((page) => page?.context?.contents ?? []) ?? [];
  const totalCount = data?.pages[0]?.context?.totalCount ?? 0;

  if (isPending) {
    return (
      <div className="h-[53.125rem] flex-1 flex-center">
        <Spinner />
      </div>
    );
  }

  if (totalCount < 1)
    return (
      <div className="h-[53.125rem] flex-1 text-center font-400 text-Gray-550 flex-center typo-body3">
        아직 등록된 기부가 없어요.
        <br />첫 번째 기부자가 되어보세요.
      </div>
    );

  return (
    <section className="flex flex-col gap-28 px-16 pt-16">
      <div className="w-full rounded-medium border border-Gray-200 bg-Gray-25 px-20 py-16 text-center font-500 text-Gray-600 typo-body4">
        총 <span className="text-B-500">{totalCount}</span>건이 기부되었습니다.
      </div>
      <ul className="flex flex-col gap-16">
        {list.map(
          ({
            homeDonationId: home_donation_id,
            daysAgo: days_ago,
            donationAmount: donation_amount,
            nickname,
            profileImage: profile_image,
          }) => (
            <li
              key={home_donation_id}
              className="flex items-center justify-between border-b border-Gray-300 pb-16"
            >
              <div className="flex items-center gap-12">
                <Avatar
                  alt="기부자 프로필 이미지"
                  size={40}
                  src={profile_image}
                />
                <div className="flex flex-col gap-2">
                  <p className="font-500 text-Gray-600 typo-body4">
                    {nickname}
                  </p>
                  <p className="font-700 text-Gray-900 typo-body4">
                    {donation_amount.toLocaleString()}원 참여
                  </p>
                </div>
              </div>
              <p className="font-400 text-Gray-600 typo-caption1">{days_ago}</p>
            </li>
          ),
        )}
      </ul>
      {hasNextPage ? (
        <div className="my-30 w-full flex-center" ref={ref}>
          <Spinner />
        </div>
      ) : null}
    </section>
  );
};
